import type { CoverageStatus } from './coverage.types';
import { daysUntil, isPast } from './coverageDates';

/**
 * The API's status vocabulary is wider than the tile's, inconsistently spaced
 * (`IN FORCE`, `IN_FORCE`) and untyped where it crosses into the console. A
 * lookup keyed on a narrow union therefore misses, and the next property access
 * throws -- so everything unrecognised lands on `unknown`, which renders as a
 * neutral pill rather than taking the page down.
 */

const normalize = (raw: unknown): string =>
  String(raw ?? '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

/**
 * The service's own vocabulary, from `PolicyStatus` in fir-common-messaging and
 * the customer allowlist in `applyCustomerFilters` -- not guessed from the
 * handful of values the fixtures happened to use.
 */
const POLICY_STATUS: Record<string, CoverageStatus> = {
  INFORCE: 'inForce',
  ACTIVE: 'inForce',
  INREINSTATEMENTPERIOD: 'inForce',
  ISSUED: 'issued',
  BOUND: 'bound',
  BOUNDREQUESTED: 'bound',
  SIGNED: 'bound',
  EXPIRED: 'expired',
  // A renewed term is over; its successor is the coverage in force.
  RENEWED: 'expired',
  BINDEREXPIRED: 'expired',
  NONRENEWED: 'expired',
  LAPSED: 'expired',
  // Still in force, but on notice -- surfaced as cancelled would overstate it,
  // so it keeps its own place in the map rather than silently reading as live.
  CANCELPENDING: 'cancelPending',
  CANCELREQUESTED: 'cancelPending',
  CANCELLED: 'cancelled',
  CANCELED: 'cancelled',
  FLATCANCELLED: 'cancelled',
  VOID: 'cancelled',
  VOIDED: 'cancelled',
  RESCIND: 'cancelled',
};

const QUOTE_STATUS: Record<string, CoverageStatus> = {
  AVAILABLE: 'quoteReady',
  READY: 'quoteReady',
  EXPIRED: 'quoteExpired',
  DECLINED: 'quoteExpired',
  LOST: 'quoteExpired',
};

interface PolicyStatusInput {
  readonly rawStatus: unknown;
  readonly effectiveDate?: string;
  readonly termEnd?: string;
  readonly isPaid?: boolean;
  readonly isAppSigned?: boolean;
  readonly now?: string | number | Date;
}

/**
 * Dates outrank the stored status where the two disagree, but only within the
 * live family: a policy still labelled IN FORCE a week after its term ended is
 * expired, whereas one that was cancelled stays cancelled no matter what its
 * dates say.
 */
export function resolvePolicyStatus({
  rawStatus,
  effectiveDate,
  termEnd,
  isPaid,
  isAppSigned,
  now = Date.now(),
}: PolicyStatusInput): CoverageStatus {
  const mapped = POLICY_STATUS[normalize(rawStatus)] ?? 'unknown';

  // cancelPending belongs here too: a policy on notice is still running, but it must not
  // fall through to the live branches below and read as plain in-force coverage.
  if (
    mapped === 'cancelled' ||
    mapped === 'cancelPending' ||
    mapped === 'expired' ||
    mapped === 'unknown'
  ) {
    return mapped;
  }

  if (isPast(termEnd, now)) {
    return 'expired';
  }

  // Unpaid or unsigned only holds coverage back *before* it incepts. A live
  // policy pending payment is still in force -- the service says so itself
  // ("Policy In Force, Pending Payment") -- and downgrading it to "bound" told
  // an insured they had no cover because an invoice was open. The outstanding
  // payment is an action, raised separately, not a coverage state.
  if (mapped === 'bound') {
    return 'bound';
  }

  if (mapped === 'issued' && (isPaid === false || isAppSigned === false)) {
    return 'bound';
  }

  const daysToInception = daysUntil(effectiveDate, now);

  // `daysUntil` is day-granular, so a policy incepting today is in force.
  if (daysToInception !== undefined && daysToInception > 0) {
    return 'issued';
  }

  return 'inForce';
}

interface QuoteStatusInput {
  readonly rawStatus: unknown;
  readonly expiresOn?: string;
  /** False when subjectivities are outstanding. */
  readonly canBind?: boolean;
  readonly now?: string | number | Date;
}

export function resolveQuoteStatus({
  rawStatus,
  expiresOn,
  canBind,
  now = Date.now(),
}: QuoteStatusInput): CoverageStatus {
  if (isPast(expiresOn, now)) {
    return 'quoteExpired';
  }

  const mapped = QUOTE_STATUS[normalize(rawStatus)] ?? 'unknown';

  if (mapped === 'quoteReady' && canBind === false) {
    return 'quoteBlocked';
  }

  return mapped;
}

const STATUS_LABELS: Record<CoverageStatus, string> = {
  inForce: 'In force',
  issued: 'Issued — starts later',
  bound: 'Bound — action needed',
  cancelPending: 'Cancellation pending',
  expired: 'Expired',
  cancelled: 'Cancelled',
  quoteReady: 'Ready to bind',
  quoteBlocked: 'Action needed',
  quoteExpired: 'Expired',
  unknown: 'Status unavailable',
};

export const statusLabel = (status: CoverageStatus): string =>
  STATUS_LABELS[status] ?? STATUS_LABELS.unknown;

/** Whether this status means coverage is actually in place right now. */
export const isLiveCoverage = (status: CoverageStatus): boolean =>
  status === 'inForce';
